import { useCallback, useEffect, useRef, useState } from 'react';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { fetchSpeechToken } from '../apiServices/speechService';

const getWebSpeechRecognition = () => window.SpeechRecognition || window.webkitSpeechRecognition || null;
const FINAL_DEDUPE_MS = 900;

export const MIC_DISABLED_MESSAGE =
    'Microphone access is disabled. Turn it on in your browser settings to continue.';

/** Normalize browser/Azure mic permission errors into a clear user-facing message. */
export function formatSpeechDictationError(error) {
    const raw =
        typeof error === 'string'
            ? error
            : error?.message || error?.errorDetails || error?.name || error?.error || '';
    const text = String(raw || '').trim();
    const key = text.toLowerCase();
    if (
        key === 'not-allowed' ||
        key === 'notallowederror' ||
        key.includes('not allowed') ||
        key.includes('permission denied') ||
        key.includes('permission-denied') ||
        (key.includes('microphone') &&
            (key.includes('denied') || key.includes('blocked') || key.includes('disabled')))
    ) {
        return MIC_DISABLED_MESSAGE;
    }
    return text || 'Speech recognition failed.';
}

export default function useSpeechDictation({ onFinalTranscript, onInterimChange, onError, language = 'en-US' }) {
    const [isListening, setIsListening] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [engine, setEngine] = useState(null);
    const interimTextRef = useRef('');

    const azureRecognizerRef = useRef(null);
    const webRecognitionRef = useRef(null);
    const shouldListenRef = useRef(false);
    const isPausedRef = useRef(false);
    const intentionalStopRef = useRef(false);
    const recentFinalsRef = useRef([]);
    const onFinalTranscriptRef = useRef(onFinalTranscript);
    const onInterimChangeRef = useRef(onInterimChange);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onFinalTranscriptRef.current = onFinalTranscript;
    }, [onFinalTranscript]);

    useEffect(() => {
        onInterimChangeRef.current = onInterimChange;
    }, [onInterimChange]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    const setInterimText = useCallback(text => {
        interimTextRef.current = text;
        onInterimChangeRef.current?.(text);
    }, []);

    const emitFinalTranscript = useCallback(text => {
        const normalized = text.trim();
        if (!normalized) {
            return;
        }

        const now = Date.now();
        recentFinalsRef.current = recentFinalsRef.current.filter(entry => now - entry.time < 3000);

        const isDuplicate = recentFinalsRef.current.some(
            entry => entry.text === normalized && now - entry.time < FINAL_DEDUPE_MS
        );
        if (isDuplicate) {
            return;
        }

        recentFinalsRef.current.push({ text: normalized, time: now });
        onFinalTranscriptRef.current?.(normalized);
    }, []);

    const stopAzureRecognizer = useCallback(() => {
        const recognizer = azureRecognizerRef.current;
        azureRecognizerRef.current = null;
        if (!recognizer) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            recognizer.stopContinuousRecognitionAsync(
                () => {
                    recognizer.close();
                    resolve();
                },
                () => {
                    recognizer.close();
                    resolve();
                }
            );
        });
    }, []);

    const detachWebRecognizer = useCallback(({ clearListenIntent = true } = {}) => {
        if (clearListenIntent) {
            shouldListenRef.current = false;
        }

        const recognition = webRecognitionRef.current;
        webRecognitionRef.current = null;
        if (!recognition) {
            return;
        }

        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        try {
            recognition.stop();
        } catch (_) {
            // ignore stop errors when recognition is already inactive
        }
    }, []);

    const stopWebRecognizer = useCallback(() => {
        detachWebRecognizer({ clearListenIntent: true });
    }, [detachWebRecognizer]);

    const stop = useCallback(async () => {
        shouldListenRef.current = false;
        isPausedRef.current = false;
        stopWebRecognizer();
        await stopAzureRecognizer();
        recentFinalsRef.current = [];
        setIsListening(false);
        setIsPaused(false);
        setInterimText('');
        setEngine(null);
    }, [setInterimText, stopAzureRecognizer, stopWebRecognizer]);

    const startWebSpeechSession = useCallback(() => {
        if (!shouldListenRef.current) {
            return;
        }

        const SpeechRecognition = getWebSpeechRecognition();
        if (!SpeechRecognition) {
            throw new Error('Speech recognition is not supported in this browser.');
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = language;

        recognition.onresult = event => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                const text = result[0]?.transcript?.trim();
                if (!text) {
                    continue;
                }
                if (result.isFinal) {
                    emitFinalTranscript(text);
                    interim = '';
                } else {
                    interim = text;
                }
            }
            setInterimText(interim);
        };

        recognition.onerror = event => {
            if (event.error === 'no-speech' || event.error === 'aborted') {
                return;
            }
            onErrorRef.current?.(formatSpeechDictationError(event.error || 'Speech recognition failed.'));
            stop();
        };

        recognition.onend = () => {
            if (webRecognitionRef.current !== recognition || !shouldListenRef.current) {
                return;
            }
            webRecognitionRef.current = null;
            startWebSpeechSession();
        };

        webRecognitionRef.current = recognition;
        recognition.start();
    }, [emitFinalTranscript, language, stop]);

    const startWebSpeech = useCallback(() => {
        const SpeechRecognition = getWebSpeechRecognition();
        if (!SpeechRecognition) {
            throw new Error('Speech recognition is not supported in this browser.');
        }

        shouldListenRef.current = true;
        startWebSpeechSession();
        setEngine('browser');
        setIsListening(true);
        setIsPaused(false);
        setInterimText('');
    }, [setInterimText, startWebSpeechSession]);

    const startAzureSpeech = useCallback(async () => {
        await stopAzureRecognizer();

        const { token, region } = await fetchSpeechToken();
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, region);
        speechConfig.speechRecognitionLanguage = language;

        const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
        const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

        recognizer.recognizing = (_sender, event) => {
            if (event.result.reason === sdk.ResultReason.RecognizingSpeech) {
                setInterimText(event.result.text || '');
            }
        };

        recognizer.recognized = (_sender, event) => {
            if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
                const text = event.result.text?.trim();
                if (text) {
                    emitFinalTranscript(text);
                }
                setInterimText('');
            }
        };

        recognizer.canceled = (_sender, event) => {
            azureRecognizerRef.current = null;

            if (intentionalStopRef.current) {
                return;
            }

            if (event.reason === sdk.CancellationReason.Error) {
                onErrorRef.current?.(
                    formatSpeechDictationError(event.errorDetails || 'Speech recognition failed.'),
                );
                stop();
            }
        };

        await new Promise((resolve, reject) => {
            recognizer.startContinuousRecognitionAsync(resolve, reject);
        });

        azureRecognizerRef.current = recognizer;
        setEngine('azure');
        setIsListening(true);
        setIsPaused(false);
        setInterimText('');
    }, [emitFinalTranscript, language, setInterimText, stop, stopAzureRecognizer]);

    const pause = useCallback(async () => {
        if (isPausedRef.current) {
            return;
        }

        if (!shouldListenRef.current && !isListening) {
            return;
        }

        isPausedRef.current = true;
        shouldListenRef.current = false;
        intentionalStopRef.current = true;

        detachWebRecognizer({ clearListenIntent: false });

        try {
            await stopAzureRecognizer();
        } finally {
            intentionalStopRef.current = false;
        }

        setInterimText('');
        setIsPaused(true);
    }, [detachWebRecognizer, isListening, setInterimText, stopAzureRecognizer]);

    const resume = useCallback(async () => {
        if (!isPausedRef.current) {
            return;
        }

        isPausedRef.current = false;
        setIsPaused(false);
        shouldListenRef.current = true;

        try {
            await startAzureSpeech();
        } catch (azureError) {
            await stopAzureRecognizer();
            try {
                startWebSpeech();
            } catch (browserError) {
                shouldListenRef.current = false;
                isPausedRef.current = false;
                setIsPaused(false);
                const message = formatSpeechDictationError(
                    azureError?.response?.data?.detail ||
                        azureError?.message ||
                        browserError?.message ||
                        'Could not resume voice dictation.',
                );
                onErrorRef.current?.(message);
                await stop();
            }
        }
    }, [startAzureSpeech, startWebSpeech, stop, stopAzureRecognizer]);

    const start = useCallback(async () => {
        if (isListening && !isPausedRef.current) {
            return;
        }

        isPausedRef.current = false;
        setIsPaused(false);
        shouldListenRef.current = true;
        recentFinalsRef.current = [];

        try {
            await startAzureSpeech();
        } catch (azureError) {
            await stopAzureRecognizer();
            try {
                startWebSpeech();
            } catch (browserError) {
                shouldListenRef.current = false;
                const message = formatSpeechDictationError(
                    azureError?.response?.data?.detail ||
                        azureError?.message ||
                        browserError?.message ||
                        'Could not start voice dictation.',
                );
                onErrorRef.current?.(message);
                await stop();
            }
        }
    }, [isListening, startAzureSpeech, startWebSpeech, stop, stopAzureRecognizer]);

    const toggle = useCallback(async () => {
        if (isListening) {
            await stop();
            return;
        }
        await start();
    }, [isListening, start, stop]);

    useEffect(() => {
        return () => {
            shouldListenRef.current = false;
            stopWebRecognizer();
            stopAzureRecognizer();
        };
    }, [stopAzureRecognizer, stopWebRecognizer]);

    return {
        isListening,
        isPaused,
        getInterimText: () => interimTextRef.current,
        engine,
        start,
        stop,
        pause,
        resume,
        toggle,
    };
}
